import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { DragAndDropFiles, InvestigationButton, ModalContainer, TemporalUploadedFile } from "../../components"
import { returnIsValid } from "../../Main/useConstants";
import { catalogByPart } from "../../../../../../apis";
import { InputCatalogue } from "../../../../../MainComponents/Catalogues/InputCatalogue";
import { getSelectionChain } from "../../../../../MainComponents/Catalogues/utils/GetSelectionChain";

const MEASURE_CATALOGUE = "IV-102103";

const initialMeasure = {
  measure: "",
  measureCatalogPath: "",
  details: "",
}

const validateForm = (form, catalogExists) => {

  const { measure, measureCatalogPath } = form;
  const errors = {};

  if (catalogExists) {
    if (!measureCatalogPath) errors.measure = true;
  } else {
    if (!measure) errors.measure = true;
  }

  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
};

export const AddMeasure = ({
  show,
  onHide,
  measures,
  setMeasures,
  selectedConclusionMeasure,
  setSelectedConclusionMeasure,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [measure, setMeasure] = useState(initialMeasure);
  const [catalogExists, setCatalogExists] = useState(null);

  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  // Consultar si existe el catálogo de medidas correctivas
  useEffect(() => {
    const checkCatalog = async () => {
      try {
        const resp = await catalogByPart({ is_own: false, catalogue: MEASURE_CATALOGUE, path: "/" });
        setCatalogExists(!resp.error && Array.isArray(resp.data) && resp.data.length > 0);
      } catch {
        setCatalogExists(false);
      }
    };
    checkCatalog();
  }, []);

  // Para editar una medida existente
  useEffect(() => {
    if (selectedConclusionMeasure !== null) {
      const currentMeasure = measures[selectedConclusionMeasure];
      setMeasure({
        ...initialMeasure,
        ...currentMeasure,
      });
    }
  }, [selectedConclusionMeasure]);

  // Resetear los valores cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        resetValues();
      }, 200);
    }
  }, [show]);

  // Validar en cada cambio
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(measure, catalogExists);
      setErrors(errors);
    }
  }, [measure]);

  const handleSave = async() => {
    setTriedToSave(true);
    const [isValid, errors] = validateForm(measure, catalogExists);
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("resources.missing_required_inputs") });
      return;
    };

    let measureToSave = { ...measure };

    // Si se usó el catálogo, resolver el label legible
    if (catalogExists && measure.measureCatalogPath) {
      try {
        const labels = await getSelectionChain({
          catalogue: MEASURE_CATALOGUE,
          isOwn: false,
          value: measure.measureCatalogPath,
        });
        measureToSave.measure = Array.isArray(labels) ? labels.join(" > ") : measure.measureCatalogPath;
      } catch {
        measureToSave.measure = measure.measureCatalogPath;
      }
    }

    if (selectedConclusionMeasure !== null) {
      const newMeasures = _.cloneDeep(measures);
      newMeasures[selectedConclusionMeasure] = measureToSave;
      setMeasures(newMeasures);
    } else {
      setMeasures([...measures, measureToSave]);
    }
    onHide();
  }

  const resetValues = () => {
    setMeasure(_.cloneDeep(initialMeasure));
    setErrors({});
    setTriedToSave(false);
    setSelectedConclusionMeasure(null);
  }

  const handleCatalogChange = (value) => {
    setMeasure((prev) => ({ ...prev, measureCatalogPath: value }));
  }

  return (
    <ModalContainer show={show} onHide={onHide}>
      <Stack spacing={2}>

        {/* Header */}
        <Box>
          <Typography fontWeight={700} fontSize={28} color={theme.palette.ethicsBlue.main}>
            {t("Conclusion.measures_corrective_modal_title")}
          </Typography>
          <Typography fontSize={16} color="oxfordBlue" >
            {t("Conclusion.measures_corrective_modal_description")}
          </Typography>

          <hr style={{ marginBottom: 0 }} />
        </Box>

        {catalogExists === false && (
          <EGInput
            label={t("Conclusion.suggestion")}
            value={measure.measure}
            onChange={(e) => setMeasure({ ...measure, measure: e.target.value })}
            required
            isValid={returnIsValid(errors?.measure, triedToSave) ?? null}
            showSuccessValidation
            showHelpText={false}
          />
        )}

        {catalogExists === true && (
          <InputCatalogue
            label={t("Conclusion.suggestion")}
            defaultValue={measure.measureCatalogPath}
            catalogue={MEASURE_CATALOGUE}
            isOwn={false}
            onChange={handleCatalogChange}
            isValid={returnIsValid(errors?.measure, triedToSave) ?? null}
          />
        )}

        <EGInput
          label={t("Conclusion.details")}
          value={measure.details}
          onChange={(e) => setMeasure({ ...measure, details: e.target.value })}
          multiline
          rows={3}
        />

        {/* Botones */}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <InvestigationButton
            label={t("Conclusion.cancel")}
            variant="main"
            outlined
            onClick={onHide}
          />
          <InvestigationButton
            label={t("Conclusion.save")}
            variant="main"
            onClick={handleSave}
          />
        </Stack>


      </Stack>
    </ModalContainer>
  )
}
